import React from 'react'
import { Box, Stack, IconButton, Typography, alpha, useTheme } from '@mui/material'
import { ArrowLeft, Share2, MoreVertical, ArrowRight } from 'lucide-react'

import { font, space } from 'settings/tokens'
import { glassEdge, glassSx } from 'utils/glass'

/** Height of the header's control row, excluding its padding. */
export const NAV_HEADER_CONTROL = 40

/** Total header height — what a page must clear when the bar is solid. */
export const NAV_HEADER_HEIGHT = NAV_HEADER_CONTROL + space.lg * 2

/**
 * The OVERLAY puck, floating on the product photograph — 32, not the bar's 40.
 *
 * Measured on the certificate page at 375: the back/share pucks were 40x40 while
 * the `Not verified` badge beside them is 130x30 and fully OPAQUE. So the chrome
 * was 33% taller than the badge AND the only glass on the image, which inverts
 * the hierarchy: back and share are utility, the verification state is the most
 * important thing overlaid on that photo.
 *
 * 32 puts the puck within 2px of the badge's height, cutting its painted area by
 * 36% (1600 -> 1024 sq px), so the badge becomes the loudest object on the image.
 *
 * ⚠️ NOT `NAV_HEADER_CONTROL`, which also defines `NAV_HEADER_HEIGHT` — the
 * SOLID bar's height and the clearance every page reserves for it. Shrinking
 * that would move the whole header, so the overlay gets its own constant.
 */
export const NAV_OVERLAY_CONTROL = 32

/**
 * The glyph inside an OVERLAY header's controls — 20, and the SAME in both of
 * that header's states.
 *
 * The bug this fixes: the certificate header scrolls from transparent to solid,
 * and the glyph was 20 under the plate but 24 once the bar went solid, so the
 * two controls visibly grew as you scrolled past the hero. Same buttons, same
 * screen, two sizes.
 *
 * Why 20 and not 24 — it is arithmetic, not taste. A puck reads as a puck at a
 * box:glyph ratio near 1.67 (the bar's own 40:24). Holding 24 therefore forces
 * the plate back to 40, which is the 33%-taller-than-the-badge problem
 * `NAV_OVERLAY_CONTROL` exists to fix. You cannot have a plate that is quieter
 * than the badge AND a 24px glyph; one of them has to give, and the glyph is
 * the one with slack — 20 is already in this app's icon scale (14/16/20/24/32),
 * and the 44px touch target is set independently, so nothing gets harder to hit.
 *
 * Scoped to `overlay` headers. A plain bar that never floats over a photograph
 * has no transition to be inconsistent across and keeps the standard 24.
 */
export const NAV_OVERLAY_GLYPH = 20

/** 44pt, iOS's minimum touch target. The 40px puck was already under it. */
const TOUCH_TARGET = 44

export interface NavigationHeaderProps {
  title?: string
  onBack?: () => void
  onShare?: () => void
  onOptions?: (event: React.MouseEvent<HTMLButtonElement>) => void
  /**
   * VRG-165 — float the controls over the product image rather than occupying a
   * bar above it, so the image can be the page's hero. VERA-315 Phase 7 made this
   * a scroll-linked pair rather than a fixed choice: see `solid`.
   */
  overlay?: boolean
  /**
   * VERA-315 Phase 7 — the scrolled state.
   *
   * `false`: transparent, controls floating on the photograph, no title.
   * `true`: glass bar with a hairline and the product title.
   *
   * The controls do not MOVE between the two — they are in the same place at the
   * same size, and only the chrome behind them changes. That is the whole trick:
   * the transition reads as a surface arriving under the buttons rather than as
   * the buttons relocating, and there is never a moment with two back arrows.
   */
  solid?: boolean
  /**
   * VERA-315 — the width of the page's DESKTOP content container, if it has one.
   *
   * Supplied, the controls stop hugging the viewport at `md+` and instead line up
   * with that container: Back's left edge on the left column's left edge, and the
   * LAST right-hand control's right edge on the right column's right edge (the
   * kebab when there is one, otherwise Share). Below `md` it changes nothing.
   *
   * Passed in rather than hardcoded because the container is the PAGE's fact, not
   * the header's — the certificate page owns `DESKTOP_MAX_WIDTH` / `DESKTOP_GUTTER`
   * and the two would drift the moment one of them moved.
   */
  contentMaxWidth?: number
  /** The container's own horizontal padding. Only read when `contentMaxWidth` is set. */
  contentGutter?: number
}

export const NavigationHeader: React.FC<NavigationHeaderProps> = ({
  title,
  onBack,
  onShare,
  onOptions,
  overlay = false,
  solid = false,
  contentMaxWidth,
  contentGutter = 0,
}) => {
  const theme = useTheme()

  /**
   * VERA-315 — align the controls to the page's content container at md+.
   *
   * PADDING, not a narrowed element: the bar is glass and full-bleed, and it has to
   * stay that way. Constraining the Stack itself to 1120 would turn the solid state
   * into a floating pill with two bare strips of page either side of it.
   *
   * `(100% - maxWidth) / 2 + gutter` reproduces exactly what the grid below does
   * (`width: 100%; max-width: M; margin-inline: auto; padding-inline: G`), so the
   * inset tracks the container at every width without either side knowing the
   * other's arithmetic. `max(gutter, …)` covers the band between `md` and `maxWidth`,
   * where the container is narrower than its cap and the expression goes negative —
   * there the grid is flush and only its gutter applies.
   *
   * A percentage, not `100vw`: percentage padding resolves against the containing
   * block (the page column this bar already sits in), which is the same box the grid
   * measures itself against. `100vw` includes the classic scrollbar, so on a platform
   * that reserves one the header would sit a scrollbar's width off the content.
   *
   * The padding stays symmetric, so the centred title's midpoint is unmoved.
   */
  const desktopInset =
    contentMaxWidth === undefined
      ? undefined
      : `max(${contentGutter}px, calc((100% - ${contentMaxWidth}px) / 2 + ${contentGutter}px))`

  /**
   * The translucent disc behind each glyph exists ONLY while the controls sit on
   * photography, where a bare icon disappears on roughly half a catalogue. Once
   * the bar is solid the disc is chrome on chrome — a pill on a panel — so it
   * goes, and the glyph takes the bar's own text colour.
   *
   * ## The material is the bottom nav's, not a lighter imitation of it
   *
   * These were `white 0.85` behind `blur(8px)`, which is not glass — it is a
   * near-opaque white chip. Three of them on a dark product photograph are three
   * high-contrast rectangles competing with the subject, which is what read as
   * "distracting" and is the thing the image-forward redesign was removing. The
   * floating-vs-bar question was downstream of this: a bar looked better mainly
   * because it gave the chips somewhere to belong.
   *
   * So they take the SAME material the floating bottom nav uses — 0.72 alpha
   * behind `blur(20px) saturate(180%)`, plus its hairline. `saturate` is the part
   * that does the work and the part that is easy to omit: blur alone greys out
   * what is behind it, so the puck looks like frosted plastic; pushing saturation
   * back up keeps the photograph's colour showing through, which is what reads as
   * glass. One material for both floating surfaces on the screen.
   *
   * CIRCULAR, not the squircle they were. A round puck reads as a floating object;
   * a rounded square reads as a small panel, i.e. as a fragment of the bar we are
   * deliberately not drawing.
   */
  /*
   * The control keeps a 44px TOUCH TARGET via a negative-inset `::after`, so the
   * hit area is decoupled from the painted size. Shrinking the puck to 32 (or
   * removing it entirely) would otherwise take the tap target down with it, and
   * 40 was already below the 44pt minimum.
   */
  // Applied in EVERY branch below, so the glyph cannot change size as the bar
  // crosses from transparent to solid. See `NAV_OVERLAY_GLYPH`.
  const overlayGlyphSx = overlay
    ? { '& svg': { width: NAV_OVERLAY_GLYPH, height: NAV_OVERLAY_GLYPH } }
    : {}

  const touchTargetSx = {
    position: 'relative' as const,
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: TOUCH_TARGET,
      height: TOUCH_TARGET,
    },
  }

  const buttonSx =
    overlay && !solid
      ? {
          /*
           * The glass puck, at 32 instead of 40.
           *
           * Opacity drops 0.72 -> 0.60 so it recedes behind the opaque badge.
           * Checked rather than assumed: a BLACK glyph on white@0.60 over a dark
           * photo composites to roughly #999, which is ~5.9:1 against black —
           * past WCAG AA for text, let alone the 3:1 non-text threshold. Over a
           * light photo it composites toward white and is better still. The
           * plate is what makes that true at any opacity, which is why this
           * control can afford to be subtle.
           *
           * A bare drop-shadowed glyph with NO plate was built and measured
           * against this (VRG-774). It ties on a dark hero and FAILS on a light
           * one: white glyph on the scrimmed image computes to 1.9:1, against
           * WCAG's 3:1 non-text floor, where the plate holds 7.4:1 (dark) to
           * 16.5:1 (light). Rescuing it needs the top scrim near 0.50 alpha — a
           * heavy vignette on every hero — so the plate won. Do not re-litigate
           * this by eye on a dark product shot; that is the case where they tie.
           */
          width: NAV_OVERLAY_CONTROL,
          height: NAV_OVERLAY_CONTROL,
          /*
           * The tenant's Studio radius, not a hardcoded circle — the same token the
           * floating bottom nav takes, so the two floating surfaces on the screen
           * agree, and a brand that chose square gets square chrome throughout.
           */
          borderRadius: `${theme.shape.borderRadius}px`,
          color: theme.palette.common.black,
          backgroundColor: alpha(theme.palette.common.white, 0.6),
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: `1px solid ${alpha(theme.palette.common.black, 0.06)} !important`,
          transition: 'background-color 200ms ease',
          '&:hover': { backgroundColor: alpha(theme.palette.common.white, 0.85) },
          // Degrade honestly where the material is unsupported: a nearly-opaque
          // disc, rather than a 60%-transparent one with an illegible glyph.
          '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))':
            { backgroundColor: alpha(theme.palette.common.white, 0.92) },
          // The glyph is authored at 24 for the 40px bar button; at 32 that
          // leaves 4px of plate around it, so it reads as a glyph in a box
          // rather than a puck. 20 restores the original ratio.
          ...overlayGlyphSx,
          ...touchTargetSx,
        }
      : {
          color: 'text.primary',
          transition: 'background-color 200ms ease',
          ...overlayGlyphSx,
        }

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='space-between'
      sx={
        overlay
          ? {
              /*
               * STICKY, not fixed — and this is load-bearing.
               *
               * A fixed element is sized by the initial containing block, which
               * is the viewport. That is one more thing to be wrong about than
               * sticky needs to be: sticky is sized by its normal containing
               * block, i.e. the page column it already sits in, so it is exactly
               * as wide as the content it belongs to and cannot be widened by
               * whatever the environment thinks the viewport is. (The dev
               * preview pane reports `innerWidth: 750` against a 375px layout
               * viewport, which stretched the fixed version to 750 and pushed
               * Share and the overflow clean off the right edge. Every MUI
               * Drawer in this app measures 750 there for the same reason.)
               *
               * Sticky also survives the transformed ancestor that breaks fixed
               * here: the page's slide transition leaves a `transform` behind
               * (`fill: both`), which reparents a fixed child's containing block
               * but does nothing to a sticky one.
               */
              position: 'sticky',
              top: 0,
              alignSelf: 'stretch',
              /*
               * Pulls the following content up by exactly this bar's height, so
               * the photograph starts at the top of the page and the controls
               * float ON it — the VRG-165 arrangement — while the bar itself is
               * still in flow and therefore sticks.
               */
              mb: `-${NAV_HEADER_HEIGHT}px`,
              // Page chrome, below MUI's drawer level (1200) so every bottom
              // sheet stays above it — the same layer the sticky action bar uses
              // at the other end of the screen.
              zIndex: 1100,
              px: 2,
              py: `${space.lg}px`,
              gap: `${space.md}px`,

              // See `desktopInset`. Longhand AFTER the `px` shorthand so it wins on
              // source order — a media query adds no specificity of its own.
              ...(desktopInset
                ? {
                    [theme.breakpoints.up('md')]: {
                      paddingLeft: desktopInset,
                      paddingRight: desktopInset,
                    },
                  }
                : {}),

              /*
               * The glass only exists in the solid state — which is what the
               * token's `active` flag is for, so the conditional is passed in
               * rather than written out three times here.
               *
               * NOTE this converts the BAR only. The floating back/share DISC
               * above keeps its own hand-written material on purpose: it is
               * 0.72 white (not 0.82 paper) with a 0.92 unsupported fallback and
               * a hover state, because it floats on a PHOTOGRAPH and has to stay
               * legible over an unknown image. That is a different problem from a
               * bar over the page, and folding it into the shared token would
               * either weaken the disc or drag photo-legibility numbers into
               * every bar in the app.
               */
              ...glassSx(theme, solid),
              borderBottom: solid ? glassEdge(theme) : '1px solid transparent !important',
              // AFTER the spread, so 200ms wins over the token's 150ms. Kept
              // deliberately: this bar CROSSES from transparent to glass as the
              // hero scrolls under it, and at 150ms that read as a snap rather
              // than a fade. The token's default suits bars that flip at a
              // scroll threshold; this one is the slower, more visible case.
              transition: 'background-color 200ms ease, border-color 200ms ease',

              /*
               * TOP SCRIM — the reason floating controls can survive without a bar.
               *
               * A translucent white puck is legible on a dark photograph and
               * invisible on a light one, and the catalogue contains both; that
               * unreliability is the strongest argument FOR a solid bar, because a
               * bar owns its background. This buys the same guarantee without
               * spending 14% of the hero on a panel: a short gradient darkens only
               * the top of whatever image is behind it, so the puck always has
               * something to sit against.
               *
               * It reads as photographic vignetting rather than as UI, which is
               * the point — the eye does not count it as another element the way
               * it counts a bar with an edge.
               *
               * Sized to twice the header so it has room to fall off to nothing.
               * A gradient that ends abruptly at the control row draws its own
               * horizontal line, which is the bar again, in shadow.
               */
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: NAV_HEADER_HEIGHT * 2,
                background:
                  'linear-gradient(to bottom, rgba(0,0,0,0.34) 0%, rgba(0,0,0,0.16) 45%, rgba(0,0,0,0) 100%)',
                // Only over the photograph. Once the bar is solid it has its own
                // surface and a shadow on top of it would be a second one.
                opacity: overlay && !solid ? 1 : 0,
                transition: 'opacity 200ms ease',
                // Never intercepts a tap meant for the image beneath it.
                pointerEvents: 'none',
                // Behind the controls, in front of the photograph.
                zIndex: -1,
              },
            }
          : { px: 1.5, py: 1 }
      }
    >
      <Stack direction='row' alignItems='center' spacing={1} sx={{ flexShrink: 0 }}>
        <IconButton aria-label='Back' onClick={onBack} size='small' sx={buttonSx}>
          <ArrowLeft size={24} />
        </IconButton>
      </Stack>

      {/* The title belongs to the solid state only. On the photograph the object
          is already identified — by the photograph — and the page's own 24px
          title is a few hundred pixels below; a second copy over the image would
          be the same name twice. It earns its place once the image is gone. */}
      {overlay && (
        <Typography
          noWrap
          sx={{
            flexGrow: 1,
            minWidth: 0,
            /*
             * LEFT, not centred — one title alignment across the whole app.
             *
             * It was `textAlign: 'center'`, which is the phone-toolbar
             * convention and was defensible in isolation: this header has
             * controls on BOTH sides (back left, Share + Actions right), and a
             * centred title is what balances that. But it made this the only
             * left-to-right-reading title in the app — every other page, and
             * every sub-view reached FROM this one, puts its title hard against
             * the content column's left edge via `PageHeader`.
             *
             * Centring also cost something concrete here rather than only being
             * inconsistent: the title is `noWrap` in a slot squeezed between two
             * control groups, so a centred long product name truncates from BOTH
             * ends toward the middle and loses the beginning of the name — which
             * is the part that identifies the product. Left-aligned, it truncates
             * only at the tail, so "Louis Vuitton Monogram Jack…" still reads.
             *
             * `ml: -8px` lands the text 12px from the ARROW GLYPH, which is
             * exactly the sub-views' arrow-to-label distance — so both surfaces
             * are an arrow followed by text at the same remove, vertically
             * centred on it (MEASURED: centre offset 0 on both).
             *
             * NEGATIVE because this row carries a 12px `column-gap` between its
             * control GROUPS (MEASURED on the flex row), and the title is a group
             * member rather than part of the back cluster. 12px of group spacing
             * plus the 40px control's own 8px glyph inset puts the text 20px from
             * the glyph; the sub-view's puck-to-label gap is 4px on a box whose
             * inset is the same 8, i.e. 12 from the glyph. The -8 is the
             * difference between a group gap and an intra-group one, not a magic
             * number.
             *
             * The title cannot start on the content column's left edge the way a
             * sub-view's does, and that is structural rather than an oversight:
             * there the title is on its OWN ROW beneath the back link, here it
             * shares the row with the arrow. Matching the arrow-to-text
             * relationship is the parity actually available.
             */
            /*
             * CENTRED on a phone, LEFT from `md` up.
             *
             * Not a hedge — the two widths are genuinely different controls. At
             * 375 this is a classic phone toolbar: a back arrow, a title, and one
             * or two icon buttons, with the title as the bar's subject. Centring
             * is that idiom, and it is what every native app the reader has ever
             * used does at this width.
             *
             * From `md` the header stops being a toolbar and becomes the top of a
             * wide content column, where the title is the column's heading and
             * belongs on its left edge with everything beneath it (working-
             * conventions §40, and the sub-views this page is reached from).
             *
             * ⚠️ CENTRED IN ITS FLEX SLOT, not in the viewport. The slot is what
             * remains between the two control groups, so the title is optically
             * centred only while those groups are the same width — true with back
             * (40) against Share (40), and NOT true when the Actions button is
             * also present. A true viewport centre needs the title absolutely
             * positioned, which costs the `noWrap` truncation working against the
             * available space. Measured and accepted; revisit if the asymmetric
             * case starts to read as an error rather than as a nudge.
             */
            textAlign: { xs: 'center', md: 'left' },
            ml: { xs: 0, md: '-8px' },
            fontSize: font.label,
            fontWeight: 500,
            color: 'text.primary',
            opacity: solid && title ? 1 : 0,
            transition: 'opacity 200ms ease',
            // Never intercepts a tap, so the invisible copy at the top of the
            // page cannot swallow a press meant for the image behind it.
            pointerEvents: 'none',
          }}
        >
          {title}
        </Typography>
      )}

      <Stack
        direction='row'
        alignItems='center'
        spacing={overlay ? 1 : 0.5}
        sx={{ flexShrink: 0 }}
      >
        {/* Handler-gated, exactly like the Actions button below it, and for the
            same reason (working-conventions §46): Share was rendering
            UNCONDITIONALLY, so a caller passing no handler got a glyph that
            silently did nothing. That was invisible while the certificate page
            was the only caller AND only rendered this bar for the owner; VERA-304
            renders it for a pending-transfer RECIPIENT, who has no Share screen to
            reach (the views endpoint is owner-scoped), and a dead button is worse
            than no button. */}
        {onShare && (
          <IconButton aria-label='Share' onClick={onShare} size='small' sx={buttonSx}>
            <Share2 size={24} />
          </IconButton>
        )}
        {/* Rendered only when a caller supplies the handler, which is how the
            certificate page suppresses it when a certificate's state leaves no
            manage actions at all (working-conventions §46 — an overflow trigger
            that opens an empty sheet is worse than no trigger).

            LAST in the row, so when it IS present the KEBAB owns the content
            column's right edge and Share sits inboard of it. That is the right way
            round: `desktopInset` aligns the control GROUP to the content column, and
            the group's edge is its outermost control, whichever that happens to be.
            Aligning Share instead would need the kebab to hang outside the column. */}
        {onOptions && (
          <IconButton
            aria-label='Actions'
            aria-haspopup='dialog'
            onClick={onOptions}
            size='small'
            sx={{
              ...buttonSx,
              /*
               * A LABELLED SECONDARY BUTTON WITH A RIGHT ARROW at md+, not a kebab.
               *
               * The kebab was miscommunicating RANK. `⋮` is the OVERFLOW idiom —
               * it means "extra, minor things that did not fit" — and these are
               * the page's primary actions. That mismatch, not the size, is why
               * it read as forgettable. A bordered control with a name and a
               * direction says "this opens a set of choices", which is what it
               * does.
               *
               * ⚠️ IT WAS A CHEVRON-DOWN, and that was a real mis-signal rather
               * than a stylistic one. A down chevron is the DROPDOWN idiom: it
               * promises a menu anchored directly beneath the control. This opens
               * a CENTRED DIALOG at md+ and a bottom sheet at xs — neither of
               * which appears below the button — so the glyph pointed somewhere
               * the surface never appears. The previous note recorded this as a
               * "known over-promise, accepted", which is the shape of a defect
               * that has been documented instead of fixed.
               *
               * A right arrow carries "this goes somewhere / opens something"
               * without naming a direction the UI then contradicts — the same
               * reason a disclosure arrow, not a dropdown caret, marks a row that
               * opens a detail surface. `aria-haspopup='dialog'` continues to
               * tell assistive tech exactly what opens.
               *
               * Icon-only at xs: 375 has no room for back (40) + share (40) + a
               * labelled control without eating the centred title. Same shape
               * the toolbar's Select button uses, so the app keeps ONE rule for
               * "a control that can afford a word when there is space".
               *
               * NOT broken out into separate buttons, deliberately. Sell and
               * Transfer are coming; a header of individual action buttons dies
               * at N=4 and sooner on a phone. When N grows and one action clearly
               * dominates in real use, break THAT one out beside this — informed,
               * rather than guessing now which deserves the slot.
               */
              width: { xs: NAV_HEADER_CONTROL, md: 'auto' },
              minWidth: NAV_HEADER_CONTROL,
              height: NAV_HEADER_CONTROL,
              px: { xs: 0, md: '14px' },
              gap: { xs: 0, md: '6px' },
              borderRadius: `${theme.shape.borderRadius}px`,
              /*
               * The secondary border only applies where the label does, and only
               * OUTSIDE overlay mode: in overlay `buttonSx` already supplies glass
               * plus its own border, so adding one here would double it.
               */
              ...(overlay && !solid
                ? {}
                : {
                    border: { xs: 'none', md: `1px solid ${theme.palette.divider}` },
                    '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.04) },
                  }),
            }}
          >
            {/*
              THE ELLIPSIS IS xs-ONLY. At md+ the label says WHAT and the chevron
              says THAT IT OPENS, so a kebab is a third element restating what the
              other two already cover — and unlike Filter or Sort, whose icons
              carry meaning, `⋮` says nothing about actions specifically. It is a
              generic "there is more" mark, which is exactly the rank this control
              stopped having when it got a name.

              It stays at xs because there the button is icon-only and the kebab
              is the ENTIRE message; removing it would leave an empty 40px square.
            */}
            <Box component='span' sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
              <MoreVertical size={24} />
            </Box>
            <Typography
              component='span'
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: 1,
                color: 'inherit',
                whiteSpace: 'nowrap',
                display: { xs: 'none', md: 'inline' },
              }}
            >
              Actions
            </Typography>
            {/*
              Hidden at xs alongside the label — an arrow beside a bare kebab
              would be two "opens something" glyphs saying it twice.

              Gated with MUI's `sx` rather than Tailwind's `md:` because the two
              scales DISAGREE. This app sets its own breakpoints
              (`settings/mui/breakpoints.ts`): **md is 720**, not MUI's default
              900 and not Tailwind's 768. So a Tailwind `md:` class here would
              flip the arrow 48px AFTER the label it belongs to, leaving a
              window where the button reads "Actions" with no affordance.

              Do not reason about "desktop" in this app from MUI's defaults — 720
              is unusually low and it is the value that actually governs.
            */}
            <Box component='span' sx={{ display: { xs: 'none', md: 'inline-flex' }, flexShrink: 0 }}>
              <ArrowRight size={16} />
            </Box>
          </IconButton>
        )}
      </Stack>
    </Stack>
  )
}

export default NavigationHeader
